import React, { useState } from 'react';
import { packageAPI, customerAPI } from '../api/axios';
import { CreditCard, Plus, Minus, Eye, Search, Package as PackageIcon, RefreshCcw, Users, X } from 'lucide-react';
import { formatNumber } from '../utils/dateUtils';
import './PackageManagementPage.css';

const PackageManagementPage = () => {
  // 상태 관리
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);

  // 모달 상태
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showDeductModal, setShowDeductModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [usageHistory, setUsageHistory] = useState([]);
  const [refundCalculation, setRefundCalculation] = useState(null);

  // 패키지 구매 폼
  const [purchaseForm, setPurchaseForm] = useState({
    packageType: 'COUNT_BASED',
    packageName: '',
    purchaseAmount: '',
    totalCount: '',
    totalAmount: '',
    startDate: new Date().toISOString().split('T')[0],
    expiryDate: '',
    discountRate: '',
    memo: ''
  });

  // 패키지 차감 폼
  const [deductForm, setDeductForm] = useState({
    usedCount: 1,
    usedAmount: '',
    description: '',
    adminName: '김다혜'
  });

  // 환불 폼
  const [refundForm, setRefundForm] = useState({
    refundReason: '',
    description: '',
    adminName: '김다혜'
  });

  // 공유 모달 상태
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedPackageForShare, setSelectedPackageForShare] = useState(null);
  const [shareForm, setShareForm] = useState({
    sharedCustomerId: '',
    shareType: 'COUPLE',
    memo: '',
    adminName: '김다혜'
  });
  const [shareSearchTerm, setShareSearchTerm] = useState('');
  const [shareSearchResults, setShareSearchResults] = useState([]);
  const [packageShares, setPackageShares] = useState([]);

  // 고객 검색
  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await customerAPI.search(searchTerm);
      setSearchResults(response.data);
    } catch (error) {
      console.error('고객 검색 실패:', error);
      alert('고객 검색에 실패했습니다.');
    }
  };

  // 고객 선택
  const handleSelectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setSearchResults([]);
    setSearchTerm('');
    await loadCustomerPackages(customer.id);
  };

  // 고객의 패키지 목록 로드
  const loadCustomerPackages = async (customerId) => {
    setLoading(true);
    try {
      const response = await packageAPI.getByCustomer(customerId);
      setPackages(response.data);
    } catch (error) {
      console.error('패키지 목록 로드 실패:', error);
      alert('패키지 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 콤마 제거 및 숫자만 추출
  const parseNumber = (value) => {
    return value.replace(/,/g, '');
  };

  // 패키지 구매
  const handlePurchase = async (e) => {
    e.preventDefault();

    if (!selectedCustomer) {
      alert('고객을 먼저 선택해주세요.');
      return;
    }

    try {
      // 만료일 계산: 미입력 시 시작일로부터 2년 후
      const calculateExpiryDate = () => {
        if (purchaseForm.expiryDate) {
          return purchaseForm.expiryDate;
        }
        const startDate = new Date(purchaseForm.startDate);
        startDate.setFullYear(startDate.getFullYear() + 2);
        return startDate.toISOString().split('T')[0];
      };

      const data = {
        ...purchaseForm,
        customerId: selectedCustomer.id,
        purchaseAmount: parseInt(parseNumber(purchaseForm.purchaseAmount)),
        totalCount: purchaseForm.packageType === 'COUNT_BASED' ? parseInt(purchaseForm.totalCount) : null,
        totalAmount: purchaseForm.packageType === 'AMOUNT_BASED' ? parseInt(parseNumber(purchaseForm.totalAmount)) : null,
        discountRate: purchaseForm.discountRate ? parseFloat(purchaseForm.discountRate) : 0,
        expiryDate: calculateExpiryDate() // 만료일 미입력 시 시작일로부터 2년 후
      };

      await packageAPI.purchase(data);
      alert('패키지가 등록되었습니다.');
      setShowPurchaseModal(false);
      setPurchaseForm({
        packageType: 'COUNT_BASED',
        packageName: '',
        purchaseAmount: '',
        totalCount: '',
        totalAmount: '',
        startDate: new Date().toISOString().split('T')[0],
        expiryDate: '',
        discountRate: '',
        memo: ''
      });
      await loadCustomerPackages(selectedCustomer.id);
    } catch (error) {
      console.error('패키지 등록 실패:', error);
      alert(error.response?.data?.message || '패키지 등록에 실패했습니다.');
    }
  };

  // 패키지 차감
  const handleDeduct = async (e) => {
    e.preventDefault();

    try {
      const data = {
        usedCount: selectedPackage.packageType === 'COUNT_BASED' ? parseInt(deductForm.usedCount) : null,
        usedAmount: selectedPackage.packageType === 'AMOUNT_BASED' ? parseInt(parseNumber(deductForm.usedAmount)) : null,
        description: deductForm.description,
        adminName: deductForm.adminName
      };

      await packageAPI.deduct(selectedPackage.id, data);
      alert('패키지가 차감되었습니다.');
      setShowDeductModal(false);
      setDeductForm({
        usedCount: 1,
        usedAmount: '',
        description: '',
        adminName: '김다혜'
      });
      await loadCustomerPackages(selectedCustomer.id);
    } catch (error) {
      console.error('패키지 차감 실패:', error);
      alert(error.response?.data?.message || '패키지 차감에 실패했습니다.');
    }
  };

  // 환불 모달 열기
  const handleOpenRefundModal = async (pkg) => {
    setSelectedPackage(pkg);
    setShowRefundModal(true);

    try {
      const response = await packageAPI.getRefundCalculation(pkg.id);
      setRefundCalculation(response.data);
    } catch (error) {
      console.error('환불 금액 계산 실패:', error);
      alert('환불 금액 계산에 실패했습니다.');
      setShowRefundModal(false);
    }
  };

  // 패키지 환불
  const handleRefund = async (e) => {
    e.preventDefault();

    if (!window.confirm('정말 환불하시겠습니까? 환불 후에는 취소할 수 없습니다.')) {
      return;
    }

    try {
      await packageAPI.refund(selectedPackage.id, refundForm);
      alert('환불이 완료되었습니다.');
      setShowRefundModal(false);
      setRefundForm({
        refundReason: '',
        description: '',
        adminName: '김다혜'
      });
      setRefundCalculation(null);
      await loadCustomerPackages(selectedCustomer.id);
    } catch (error) {
      console.error('환불 실패:', error);
      alert(error.response?.data?.message || '환불에 실패했습니다.');
    }
  };

  // 패키지 상세 보기
  const handleViewDetail = async (pkg) => {
    setSelectedPackage(pkg);
    setShowDetailModal(true);

    try {
      const response = await packageAPI.getHistory(pkg.id);
      setUsageHistory(response.data);
    } catch (error) {
      console.error('사용 이력 로드 실패:', error);
    }
  };

  // 공유 모달 열기
  const handleOpenShareModal = async (pkg) => {
    setSelectedPackageForShare(pkg);
    setShowShareModal(true);

    try {
      const response = await packageAPI.getPackageShares(pkg.id);
      setPackageShares(response.data);
    } catch (error) {
      console.error('공유 목록 조회 실패:', error);
    }
  };

  // 공유 모달 닫기
  const handleCloseShareModal = () => {
    setShowShareModal(false);
    setSelectedPackageForShare(null);
    setShareForm({
      sharedCustomerId: '',
      shareType: 'COUPLE',
      memo: '',
      adminName: '김다혜'
    });
    setShareSearchTerm('');
    setShareSearchResults([]);
    setPackageShares([]);
  };

  // 공유할 고객 검색
  const handleShareSearch = async () => {
    if (!shareSearchTerm.trim()) {
      alert('검색어를 입력하세요.');
      return;
    }

    try {
      const response = await customerAPI.search(shareSearchTerm);
      setShareSearchResults(response.data);
    } catch (error) {
      console.error('고객 검색 실패:', error);
      alert('고객 검색에 실패했습니다.');
    }
  };

  // 공유받을 고객 선택
  const handleSelectShareCustomer = (customer) => {
    setShareForm({ ...shareForm, sharedCustomerId: customer.id });
    setShareSearchTerm(`${customer.name} (${customer.phone})`);
    setShareSearchResults([]);
  };

  // 공유 추가
  const handleAddShare = async () => {
    if (!shareForm.sharedCustomerId) {
      alert('공유받을 고객을 선택하세요.');
      return;
    }

    try {
      await packageAPI.sharePackage(selectedPackageForShare.id, shareForm);
      alert('패키지 공유가 완료되었습니다.');

      // 공유 목록 새로고침
      const response = await packageAPI.getPackageShares(selectedPackageForShare.id);
      setPackageShares(response.data);

      // 폼 초기화
      setShareForm({
        sharedCustomerId: '',
        shareType: 'COUPLE',
        memo: '',
        adminName: '김다혜'
      });
      setShareSearchTerm('');
    } catch (error) {
      console.error('공유 추가 실패:', error);
      alert(error.response?.data?.message || '공유 추가에 실패했습니다.');
    }
  };

  // 공유 해제
  const handleUnshare = async (shareId) => {
    if (!window.confirm('정말 공유를 해제하시겠습니까?')) {
      return;
    }

    try {
      await packageAPI.unsharePackage(shareId);
      alert('공유가 해제되었습니다.');

      // 공유 목록 새로고침
      const response = await packageAPI.getPackageShares(selectedPackageForShare.id);
      setPackageShares(response.data);
    } catch (error) {
      console.error('공유 해제 실패:', error);
      alert('공유 해제에 실패했습니다.');
    }
  };

  // 공유 타입 한글 변환
  const getShareTypeLabel = (type) => {
    switch (type) {
      case 'COUPLE': return '연인/부부';
      case 'FAMILY': return '가족';
      case 'FRIEND': return '지인';
      default: return type;
    }
  };

  // 활성 패키지와 비활성 패키지 분리
  const activePackages = packages.filter(pkg => pkg.status === 'ACTIVE' && pkg.isUsable);
  const inactivePackages = packages.filter(pkg => pkg.status !== 'ACTIVE' || !pkg.isUsable);

  // 날짜 포맷
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR');
  };

  // D-day 계산
  const getDday = (days) => {
    if (days < 0) return '만료됨';
    if (days === 0) return 'D-Day';
    return `D-${days}`;
  };

  return (
    <div className="package-management-page">
      <div className="page-header">
        <div className="header-title">
          <PackageIcon size={28} />
          <h1>패키지 관리</h1>
        </div>
        {selectedCustomer && (
          <button
            className="btn-purchase"
            onClick={() => setShowPurchaseModal(true)}
          >
            <Plus size={18} />
            패키지 구매
          </button>
        )}
      </div>

      {/* 고객 검색 */}
      <div className="search-section">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="고객 이름 또는 전화번호로 검색"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button onClick={handleSearch}>검색</button>
        </div>

        {searchResults.length > 0 && (
          <div className="search-results">
            {searchResults.map(customer => (
              <div
                key={customer.id}
                className="search-result-item"
                onClick={() => handleSelectCustomer(customer)}
              >
                <strong>{customer.name}</strong>
                <span>{customer.phone}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 선택된 고객 정보 */}
      {selectedCustomer && (
        <div className="selected-customer">
          <div className="customer-info">
            <h3>{selectedCustomer.name}</h3>
            <span>{selectedCustomer.phone}</span>
          </div>
          <div className="customer-stats">
            <span>방문: {selectedCustomer.visitCount}회</span>
            <span>가입일: {formatDate(selectedCustomer.createdAt)}</span>
          </div>
        </div>
      )}

      {/* 패키지 목록 */}
      {selectedCustomer && (
        <div className="packages-section">
          {/* 활성 패키지 */}
          {activePackages.length > 0 && (
            <div className="package-group">
              <h2>활성 패키지 ({activePackages.length})</h2>
              <div className="package-list">
                {activePackages.map(pkg => (
                  <div key={pkg.id} className="package-card active">
                    <div className="package-header">
                      <div className="package-name">
                        <CreditCard size={20} />
                        <span>{pkg.packageName}</span>
                      </div>
                      <span className={`package-badge ${pkg.packageType}`}>
                        {pkg.packageType === 'COUNT_BASED' ? '횟수권' : '정액권'}
                      </span>
                    </div>
                    <div className="package-body">
                      <div className="package-remaining">
                        {pkg.packageType === 'COUNT_BASED' ? (
                          <div className="remaining-info">
                            <span className="remaining-value">{pkg.remainingCount}/{pkg.totalCount}회 남음</span>
                            <div className="progress-bar">
                              <div
                                className="progress-fill"
                                style={{ width: `${(pkg.remainingCount / pkg.totalCount) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                        ) : (
                          <div className="remaining-info">
                            <span className="remaining-value">
                              {formatNumber(pkg.remainingAmount)}/{formatNumber(pkg.totalAmount)}원 남음
                            </span>
                            <div className="progress-bar">
                              <div
                                className="progress-fill"
                                style={{ width: `${(pkg.remainingAmount / pkg.totalAmount) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="package-meta">
                        <span>만료: {formatDate(pkg.expiryDate)} ({getDday(pkg.daysUntilExpiry)})</span>
                        <span>구매: {formatNumber(pkg.purchaseAmount)}원</span>
                      </div>
                    </div>
                    <div className="package-actions">
                      <button
                        className="btn-deduct"
                        onClick={() => {
                          setSelectedPackage(pkg);
                          setShowDeductModal(true);
                        }}
                      >
                        <Minus size={16} />
                        차감
                      </button>
                      <button
                        className="btn-refund"
                        onClick={() => handleOpenRefundModal(pkg)}
                      >
                        <RefreshCcw size={16} />
                        환불
                      </button>
                      <button
                        className="btn-share"
                        onClick={() => handleOpenShareModal(pkg)}
                      >
                        <Users size={16} />
                        공유
                      </button>
                      <button
                        className="btn-detail"
                        onClick={() => handleViewDetail(pkg)}
                      >
                        <Eye size={16} />
                        상세
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 만료/소진 패키지 */}
          {inactivePackages.length > 0 && (
            <div className="package-group">
              <h2>만료/소진 패키지 ({inactivePackages.length})</h2>
              <div className="package-list">
                {inactivePackages.map(pkg => (
                  <div key={pkg.id} className="package-card inactive">
                    <div className="package-header">
                      <div className="package-name">
                        <CreditCard size={20} />
                        <span>{pkg.packageName}</span>
                      </div>
                      <span className={`package-status ${pkg.status.toLowerCase()}`}>
                        {pkg.status === 'EXPIRED' ? '만료됨' : '소진됨'}
                      </span>
                    </div>
                    <div className="package-body">
                      <div className="package-meta">
                        <span>구매일: {formatDate(pkg.createdAt)}</span>
                      </div>
                    </div>
                    <div className="package-actions">
                      <button
                        className="btn-detail"
                        onClick={() => handleViewDetail(pkg)}
                      >
                        <Eye size={16} />
                        상세
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {packages.length === 0 && !loading && (
            <div className="empty-state">
              <PackageIcon size={64} />
              <p>보유 중인 패키지가 없습니다.</p>
              <button
                className="btn-primary"
                onClick={() => setShowPurchaseModal(true)}
              >
                <Plus size={18} />
                첫 패키지 등록하기
              </button>
            </div>
          )}
        </div>
      )}

      {!selectedCustomer && (
        <div className="empty-state">
          <Search size={64} />
          <p>고객을 검색하여 패키지를 관리하세요.</p>
        </div>
      )}

      {/* 패키지 구매 모달 */}
      {showPurchaseModal && (
        <div className="modal-overlay" onClick={() => setShowPurchaseModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>패키지 구매</h2>
              <button onClick={() => setShowPurchaseModal(false)}>×</button>
            </div>
            <form onSubmit={handlePurchase}>
              <div className="form-group">
                <label>패키지 타입 *</label>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      value="COUNT_BASED"
                      checked={purchaseForm.packageType === 'COUNT_BASED'}
                      onChange={(e) => setPurchaseForm({...purchaseForm, packageType: e.target.value})}
                    />
                    횟수권
                  </label>
                  <label>
                    <input
                      type="radio"
                      value="AMOUNT_BASED"
                      checked={purchaseForm.packageType === 'AMOUNT_BASED'}
                      onChange={(e) => setPurchaseForm({...purchaseForm, packageType: e.target.value})}
                    />
                    정액권
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>패키지 이름 *</label>
                <input
                  type="text"
                  value={purchaseForm.packageName}
                  onChange={(e) => setPurchaseForm({...purchaseForm, packageName: e.target.value})}
                  placeholder="예: 얼굴 관리 10회권"
                  required
                />
              </div>

              {purchaseForm.packageType === 'COUNT_BASED' ? (
                <div className="form-group">
                  <label>총 횟수 *</label>
                  <input
                    type="number"
                    value={purchaseForm.totalCount}
                    onChange={(e) => setPurchaseForm({...purchaseForm, totalCount: e.target.value})}
                    placeholder="10"
                    min="1"
                    required
                  />
                </div>
              ) : (
                <div className="form-group">
                  <label>총 금액 *</label>
                  <input
                    type="text"
                    value={purchaseForm.totalAmount ? formatNumber(purchaseForm.totalAmount) : ''}
                    onChange={(e) => {
                      const numericValue = parseNumber(e.target.value);
                      if (/^\d*$/.test(numericValue)) {
                        setPurchaseForm({...purchaseForm, totalAmount: numericValue});
                      }
                    }}
                    placeholder="1,000,000"
                    required
                  />
                </div>
              )}

              <div className="form-group">
                <label>결제 금액 *</label>
                <input
                  type="text"
                  value={purchaseForm.purchaseAmount ? formatNumber(purchaseForm.purchaseAmount) : ''}
                  onChange={(e) => {
                    const numericValue = parseNumber(e.target.value);
                    if (/^\d*$/.test(numericValue)) {
                      setPurchaseForm({...purchaseForm, purchaseAmount: numericValue});
                    }
                  }}
                  placeholder="300,000"
                  required
                />
              </div>

              <div className="form-group">
                <label>할인율 (%)</label>
                <input
                  type="number"
                  value={purchaseForm.discountRate}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
                      setPurchaseForm({...purchaseForm, discountRate: value});
                    }
                  }}
                  placeholder="10"
                  min="0"
                  max="100"
                  step="0.1"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>시작일 *</label>
                  <input
                    type="date"
                    value={purchaseForm.startDate}
                    onChange={(e) => setPurchaseForm({...purchaseForm, startDate: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>만료일 (미입력 시 시작일로부터 2년)</label>
                  <input
                    type="date"
                    value={purchaseForm.expiryDate}
                    onChange={(e) => setPurchaseForm({...purchaseForm, expiryDate: e.target.value})}
                  />
                  <small className="form-text text-muted">
                    * 미입력 시 시작일로부터 2년 후로 자동 설정됩니다.
                  </small>
                </div>
              </div>

              <div className="form-group">
                <label>메모</label>
                <textarea
                  value={purchaseForm.memo}
                  onChange={(e) => setPurchaseForm({...purchaseForm, memo: e.target.value})}
                  placeholder="VIP 특별 할인, 웨딩 이벤트 패키지 등"
                  rows="3"
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowPurchaseModal(false)}>
                  취소
                </button>
                <button type="submit" className="btn-primary">
                  등록
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 패키지 차감 모달 */}
      {showDeductModal && selectedPackage && (
        <div className="modal-overlay" onClick={() => setShowDeductModal(false)}>
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>패키지 차감</h2>
              <button onClick={() => setShowDeductModal(false)}>×</button>
            </div>
            <form onSubmit={handleDeduct}>
              <div className="package-info-box">
                <h3>{selectedPackage.packageName}</h3>
                <p>
                  {selectedPackage.packageType === 'COUNT_BASED'
                    ? `잔여: ${selectedPackage.remainingCount}/${selectedPackage.totalCount}회`
                    : `잔여: ${formatNumber(selectedPackage.remainingAmount)}/${formatNumber(selectedPackage.totalAmount)}원`}
                </p>
              </div>

              {selectedPackage.packageType === 'COUNT_BASED' ? (
                <div className="form-group">
                  <label>차감할 횟수 *</label>
                  <input
                    type="number"
                    value={deductForm.usedCount}
                    onChange={(e) => setDeductForm({...deductForm, usedCount: e.target.value})}
                    min="1"
                    max={selectedPackage.remainingCount}
                    required
                  />
                </div>
              ) : (
                <div className="form-group">
                  <label>차감할 금액 *</label>
                  <input
                    type="text"
                    value={deductForm.usedAmount ? formatNumber(deductForm.usedAmount) : ''}
                    onChange={(e) => {
                      const numericValue = parseNumber(e.target.value);
                      if (/^\d*$/.test(numericValue) && parseInt(numericValue || 0) <= selectedPackage.remainingAmount) {
                        setDeductForm({...deductForm, usedAmount: numericValue});
                      }
                    }}
                    placeholder="50,000"
                    required
                  />
                </div>
              )}

              <div className="form-group">
                <label>사유/메모</label>
                <input
                  type="text"
                  value={deductForm.description}
                  onChange={(e) => setDeductForm({...deductForm, description: e.target.value})}
                  placeholder="얼굴 관리 + 목 관리"
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowDeductModal(false)}>
                  취소
                </button>
                <button type="submit" className="btn-primary">
                  차감
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 환불 모달 */}
      {showRefundModal && selectedPackage && refundCalculation && (
        <div className="modal-overlay" onClick={() => setShowRefundModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>패키지 환불</h2>
              <button onClick={() => setShowRefundModal(false)}>×</button>
            </div>
            <form onSubmit={handleRefund}>
              <div className="package-info-box">
                <h3>{selectedPackage.packageName}</h3>
                <p>
                  {selectedPackage.packageType === 'COUNT_BASED'
                    ? `잔여: ${selectedPackage.remainingCount}/${selectedPackage.totalCount}회`
                    : `잔여: ${formatNumber(selectedPackage.remainingAmount)}/${formatNumber(selectedPackage.totalAmount)}원`}
                </p>
              </div>

              <div className="refund-calculation-box">
                <h4>환불 금액 계산</h4>
                <div className="calculation-details">
                  <div className="calculation-row">
                    <span>결제 금액:</span>
                    <span>{formatNumber(refundCalculation.totalValue)}원</span>
                  </div>
                  <div className="calculation-row">
                    <span>사용률:</span>
                    <span>{refundCalculation.usageRate.toFixed(1)}%</span>
                  </div>
                  <div className="calculation-row">
                    <span>사용 금액:</span>
                    <span>{formatNumber(refundCalculation.usedValue)}원</span>
                  </div>
                  <div className="calculation-row total">
                    <span>환불 금액:</span>
                    <span className="refund-amount">{formatNumber(refundCalculation.refundAmount)}원</span>
                  </div>
                </div>
                <p className="calculation-message">{refundCalculation.message}</p>
              </div>

              <div className="form-group">
                <label>환불 사유 *</label>
                <input
                  type="text"
                  value={refundForm.refundReason}
                  onChange={(e) => setRefundForm({...refundForm, refundReason: e.target.value})}
                  placeholder="고객 요청"
                  required
                />
              </div>

              <div className="form-group">
                <label>추가 설명</label>
                <textarea
                  value={refundForm.description}
                  onChange={(e) => setRefundForm({...refundForm, description: e.target.value})}
                  placeholder="추가 사항이 있으면 입력하세요"
                  rows="3"
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowRefundModal(false)}>
                  취소
                </button>
                <button type="submit" className="btn-danger">
                  환불 처리
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 패키지 상세 모달 */}
      {showDetailModal && selectedPackage && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>패키지 상세 정보</h2>
              <button onClick={() => setShowDetailModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h3>기본 정보</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="label">패키지명:</span>
                    <span>{selectedPackage.packageName}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">타입:</span>
                    <span>{selectedPackage.packageType === 'COUNT_BASED' ? '횟수권' : '정액권'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">결제금액:</span>
                    <span>{formatNumber(selectedPackage.purchaseAmount)}원</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">구매일:</span>
                    <span>{formatDate(selectedPackage.createdAt)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">유효기간:</span>
                    <span>{formatDate(selectedPackage.startDate)} ~ {formatDate(selectedPackage.expiryDate)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">상태:</span>
                    <span className={`status-badge ${selectedPackage.status.toLowerCase()}`}>
                      {selectedPackage.status === 'ACTIVE' ? '활성' :
                       selectedPackage.status === 'EXPIRED' ? '만료' : '소진'}
                    </span>
                  </div>
                </div>
                {selectedPackage.memo && (
                  <div className="detail-memo">
                    <span className="label">메모:</span>
                    <p>{selectedPackage.memo}</p>
                  </div>
                )}
              </div>

              <div className="detail-section">
                <h3>사용 이력</h3>
                {usageHistory.length > 0 ? (
                  <div className="history-table">
                    <table>
                      <thead>
                        <tr>
                          <th>일시</th>
                          <th>차감</th>
                          <th>잔여</th>
                          <th>사유</th>
                          <th>담당자</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usageHistory.map(history => (
                          <tr key={history.id}>
                            <td>{new Date(history.usedAt).toLocaleString('ko-KR')}</td>
                            <td>
                              {history.usageType === 'COUNT_USAGE'
                                ? `${history.usedCount}회`
                                : `${formatNumber(history.usedAmount)}원`}
                            </td>
                            <td>
                              {history.usageType === 'COUNT_USAGE'
                                ? `${history.afterCount}회`
                                : `${formatNumber(history.afterAmount)}원`}
                            </td>
                            <td>{history.description || '-'}</td>
                            <td>{history.adminName}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="no-history">사용 이력이 없습니다.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 패키지 공유 모달 */}
      {showShareModal && selectedPackageForShare && (
        <div className="modal-overlay" onClick={handleCloseShareModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>패키지 공유</h2>
              <button onClick={handleCloseShareModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="package-info-box">
                <h3>{selectedPackageForShare.packageName}</h3>
                <p>
                  {selectedPackageForShare.packageType === 'COUNT_BASED'
                    ? `잔여: ${selectedPackageForShare.remainingCount}/${selectedPackageForShare.totalCount}회`
                    : `잔여: ${formatNumber(selectedPackageForShare.remainingAmount)}/${formatNumber(selectedPackageForShare.totalAmount)}원`}
                </p>
              </div>

              <div className="share-section">
                <h4>공유 추가</h4>
                <div className="form-group">
                  <label>공유받을 고객 검색 *</label>
                  <div className="search-box">
                    <Search size={20} />
                    <input
                      type="text"
                      placeholder="고객 이름 또는 전화번호"
                      value={shareSearchTerm}
                      onChange={(e) => setShareSearchTerm(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleShareSearch()}
                    />
                    <button onClick={handleShareSearch}>검색</button>
                  </div>
                  {shareSearchResults.length > 0 && (
                    <div className="search-results">
                      {shareSearchResults.map(customer => (
                        <div
                          key={customer.id}
                          className="search-result-item"
                          onClick={() => handleSelectShareCustomer(customer)}
                        >
                          <strong>{customer.name}</strong>
                          <span>{customer.phone}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>공유 관계 *</label>
                  <select
                    value={shareForm.shareType}
                    onChange={(e) => setShareForm({...shareForm, shareType: e.target.value})}
                  >
                    <option value="COUPLE">연인/부부</option>
                    <option value="FAMILY">가족</option>
                    <option value="FRIEND">지인</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>메모</label>
                  <input
                    type="text"
                    value={shareForm.memo}
                    onChange={(e) => setShareForm({...shareForm, memo: e.target.value})}
                    placeholder="공유 사유 등"
                  />
                </div>

                <button
                  className="btn-primary"
                  onClick={handleAddShare}
                  disabled={!shareForm.sharedCustomerId}
                >
                  <Plus size={16} />
                  공유 추가
                </button>
              </div>

              <div className="share-list-section">
                <h4>현재 공유 목록</h4>
                {packageShares.length > 0 ? (
                  <div className="share-list">
                    {packageShares.map(share => (
                      <div key={share.id} className="share-item">
                        <div className="share-info">
                          <strong>{share.sharedCustomerName}</strong>
                          <span>{share.sharedCustomerPhone}</span>
                          <span className="share-type">{getShareTypeLabel(share.shareType)}</span>
                          {share.memo && <span className="share-memo">{share.memo}</span>}
                        </div>
                        <button
                          className="btn-unshare"
                          onClick={() => handleUnshare(share.id)}
                        >
                          <X size={16} />
                          해제
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-shares">공유된 고객이 없습니다.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackageManagementPage;
